import { useState, useEffect } from 'react';

import { getContext, isContextAvailable } from '@/miniappContext';

export interface AuthUser {
  isAuthorized: boolean;
  isLoading: boolean;
}

// Fallback only: on boot the auth stream emits a transient `false` before the
// real value lands, so we keep the loader up until a positive `true` signal
// (the data we're waiting for). This cap stops the loader hanging forever for
// genuinely signed-out users, who never get a `true`.
const MAX_LOADER_MS = 2500;
const TRANSIENT_FALSE_MS = 400;

export function useAuthUser(): AuthUser {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isContextAvailable()) {
      setIsLoading(false);
      return;
    }

    const ctx = getContext();
    const loaderTimer = setTimeout(() => setIsLoading(false), MAX_LOADER_MS);
    let signOutTimer: ReturnType<typeof setTimeout> | undefined;

    const resolveAuthed = () => {
      clearTimeout(loaderTimer);
      clearTimeout(signOutTimer);
      setIsAuthorized(true);
      setIsLoading(false);
    };

    const handleSignedOut = () => {
      clearTimeout(signOutTimer);
      signOutTimer = setTimeout(() => {
        setIsAuthorized(false);
        setIsLoading(false);
      }, TRANSIENT_FALSE_MS);
    };

    if (ctx.general.auth.getState().id != null) resolveAuthed();

    const unsubscribe = ctx.general.auth.subscribe(authInfo => {
      if (authInfo?.id != null) resolveAuthed();
      else handleSignedOut();
    });

    return () => {
      clearTimeout(loaderTimer);
      clearTimeout(signOutTimer);
      unsubscribe();
    };
  }, []);

  return { isAuthorized, isLoading };
}
